/**
 * Which mouse button opens a pan-drag on COMBAT.app's board, and what a finished pan owes the
 * click that follows it. {@link CombatStage} owns the pointer wiring (`beginPan`/`onPanMove`/
 * `onPanEnd`, the view transform, the window-level listeners); everything *decided* about the
 * button lives here so it is unit-tested without a canvas — the same split
 * {@link combatAuthoring} makes for the tile/wall gestures.
 *
 * **One pan implementation, four ways in.** The board's pan is a single mechanism; the button
 * rules are the only thing that varies:
 *   - **middle-drag** (button 1) — always, in any tool;
 *   - **right-drag** (button 2) — always, in any tool (the same always-available hand as middle,
 *     on the button a VTT user reaches for; it carries no other gesture on this surface);
 *   - **space-held + left-drag** — a transient hand, in any tool;
 *   - the sticky rail **"Pan" tool** — a hand on the left button.
 * The last two are the `canPanLeft` argument: {@link CombatStage} resolves `tool === "pan" ||
 * spaceHeld` and passes the boolean, so this module never has to know about tools or key state.
 *
 * The left button is otherwise reserved for the armed tool (select/move, paint, wall, ping), which
 * is why it pans only under `canPanLeft` while middle and right pan unconditionally.
 */

/**
 * Does a press of `button` open a pan-drag? `canPanLeft` is the caller's already-resolved
 * "the left button is currently a hand" state (the sticky Pan tool or a held space).
 *
 * Buttons other than 0/1/2 (the back/forward thumb buttons, 3 and 4) never pan.
 */
export const startsPan = (button: number, canPanLeft: boolean): boolean =>
  button === 1 || button === 2 || (button === 0 && canPanLeft);

/**
 * The whole pointer-down decision: does this press actually open a pan? {@link startsPan}'s button
 * rule, AND no other board gesture already in flight.
 *
 * `gestureOpen` is the caller's already-resolved "one of the left-drag gestures owns the surface
 * right now" — a token drag, a freehand draw, a tile stroke or box, a wall run, a room box, or a
 * ping hold. Every one of them commits on the matching release, so opening a pan on top of one
 * strands it: the pan takes over the window-level `mouseup`, the gesture's own ref is never
 * cleared, and it either commits later against a moved board or leaves the surface half-open. The
 * guard is deliberately "decline the pan", not "cancel the gesture" — the gesture the user is
 * already making wins, and nothing new has to be invented to arbitrate.
 *
 * Reachable in practice only via middle/right (the left button can't be pressed twice, and while
 * `canPanLeft` holds a left press pans instead of opening a gesture at all), but the rule is stated
 * for every button because the reason is about the OPEN gesture, not about which button is new.
 *
 * Deliberately NOT counted as an open gesture: an in-progress **polyline**. It accumulates by
 * click, not by drag — there's no held press to strand, and it's finished by double-click/Enter/Esc
 * — so panning between its vertices is a legitimate thing to want, and blocking it would be a
 * regression rather than a guard.
 */
export const opensPan = (button: number, canPanLeft: boolean, gestureOpen: boolean): boolean =>
  !gestureOpen && startsPan(button, canPanLeft);

/**
 * Does a press of `button` need its default action cancelled to pan cleanly? Middle-press is the
 * browser's autoscroll gesture and right-press opens the native context menu — both would fight
 * the drag, so both are `preventDefault`ed. The left button has no default to cancel here.
 *
 * The right button's *menu* additionally has to be suppressed on the `contextmenu` event itself
 * (Chrome raises it independently of `mousedown`); {@link CombatStage} wires that on the Pixi host
 * element only, so right-click keeps working everywhere else in the app.
 */
export const panNeedsPreventDefault = (button: number): boolean => button === 1 || button === 2;

/**
 * After a pan-drag ends, should the trailing `click` be swallowed?
 *
 * Only for the LEFT button, and only when the gesture actually moved — a stationary press isn't a
 * pan and its click should behave normally. The button check is load-bearing, not defensive: the
 * DOM fires `click` for the **primary button only** (a middle/right release raises `auxclick`
 * instead), so arming the swallow after a middle- or right-drag would leave the flag set with no
 * click coming to consume it — and it would then eat the user's *next* genuine left click on the
 * board (a deselect that silently doesn't happen).
 */
export const panSuppressesClick = (button: number, moved: boolean): boolean => moved && button === 0;
